import { useLayoutEffect, useRef, useState } from "react";
import { Check, CircleStop, Copy, MessageSquareOff, Play, Quote, RotateCcw, Volume2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ChatErrorMessage } from "@lexiun/lx-llm-errors";
import type { ChatMessage } from "@/types/gateway";
import type { ContentBlock } from "@/types/content-blocks";
import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { BlockRenderer } from "@/components/chat/BlockRenderer";
import { TurnActivity } from "@/components/chat/TurnActivity";
import type { TurnActivity as TurnActivityState } from "@/stores/useChatStore";
import { ChatLLMProviderErrorCard } from "@/components/chat/ChatLLMProviderErrorCard";
import { buildSelectionPreview } from "@/stores/useSelectionContextStore";
import {
  AttachmentEchoChip,
  isAudioAttachment,
  useAttachmentAudio,
} from "@/components/workspace/attachment-echo";
import { useWorkspaceFileStore } from "@/stores/useWorkspaceFileStore";
import { useWorkspaceAliasMap } from "@/hooks/useWorkspaceAliasMap";
import { MentionChip } from "@/components/workspace/MentionChip";
import { cn } from "@/lib/utils";
import { isSystemReceipt } from "@/lib/chat-receipt";
import { formatMessageTimestamp, formatMessageTimestampFull } from "@/lib/relative-date";

/** Extract a plain-text preview of a message — used for FeedbackButton's
 *  `messageExcerpt` + CopyButton's clipboard payload. Concatenates the
 *  text blocks; ignores tool_use / thinking / etc. (which have their
 *  own copy affordances if needed). */
function plainTextOf(message: ChatMessage): string {
  return message.content
    .filter((b) => b.type === "text")
    .map((b) => (b.type === "text" ? b.text : ""))
    .join("\n");
}

/** Assistant prose treatment (plan §6.6) — no bubble, slim neutral
 *  left-accent, Source Serif 4 on the warm canvas. Shared by the landed
 *  assistant turn AND the mid-stream block so the two never drift. */
// Claude-style: the AI response IS the main content on the page —
// serif reading font, no author label, no left-accent bar. The
// chrome around the assistant prose is intentionally absent so the
// text reads like a document, not a chat bubble.
const ASSISTANT_PROSE =
  "min-w-0 whitespace-pre-wrap font-reading text-body-lg text-coffee-900 dark:text-cream-100";

/** Split a finalized assistant turn into its activity trail and its answer.
 *
 *  `finalizeRun` merges as `[...liveToolBlocks, ...answerBlocks]`, so the
 *  LEADING run of `tool_use` blocks is the trail. Deliberately a leading-run
 *  scan rather than a filter: a tool block that appears AFTER text is part of
 *  the answer's own narrative (a mid-answer step) and pulling it up into the
 *  header would reorder what the lawyer read. */
export function splitTurnActivity(content: ContentBlock[]): {
  trail: ContentBlock[];
  answer: ContentBlock[];
} {
  let i = 0;
  while (i < content.length && content[i].type === "tool_use") i++;
  return { trail: content.slice(0, i), answer: content.slice(i) };
}

interface ChatMessageListProps {
  messages: ChatMessage[];
  /** Mid-flight assistant content as typed blocks (post chat-content-
   *  model redesign; replaces the legacy `streamingText: string`). */
  streamingBlocks: ContentBlock[];
  isStreaming: boolean;
  /** Live work state of the in-flight turn (L0+L1) — drives the in-flow status
   *  line and the freezing work header at the transcript tail. */
  activity?: TurnActivityState;
  /** A classified `ChatErrorMessage` envelope (renders the rich provider-error
   *  card) or a raw string (legacy flat-card fallback). */
  error: string | ChatErrorMessage | null;
  /** Stall affordance (operator directive 2026-08-14) — threaded to the LIVE
   *  TurnActivity, which renders it inside the turn rather than as a banner. */
  stalled?: boolean;
  onReleaseStalledRun?: () => void;
  /** Re-run the most recent turn (re-sends the last user prompt). When
   *  provided, a retry icon joins the action row UNDER the most recent
   *  assistant message only — retrying an older turn would re-send the
   *  latest prompt, not that turn's, so we don't offer it there. Hidden
   *  while a turn is in flight. */
  onRetry?: () => void;
}

export function ChatMessageList({ messages, streamingBlocks, isStreaming, activity, error, stalled, onReleaseStalledRun, onRetry }: ChatMessageListProps) {
  const { t } = useTranslation();

  const isStreamingActive = streamingBlocks.length > 0;

  // ⚠ NO SCROLL EFFECT HERE — deliberately, and it is not an omission.
  //
  // This component used to force `bottomRef.scrollIntoView()` on
  // `[messages.length, isStreamingActive]`. That made it a SECOND owner of the
  // transcript's scroll position, competing with `ConversationShell`'s
  // `useStickToBottom`, and the two re-armed each other's state: the smooth
  // animation landed short of a still-growing tail (so the pin latched off and
  // rows stopped being followed), while any pin fired a scroll event that read
  // as intent (so a reader who had scrolled up got jerked back down). The
  // operator saw both faces of it on 2026-08-13.
  //
  // `useStickToBottom` now owns scrolling outright: a new message is content
  // growth like any other, and it follows the bottom unless the reader has
  // scrolled away. If you are about to add a `scrollIntoView` here, you are
  // re-creating that fight.

  // The in-flow status line exists for the whole turn — from send, before any
  // content arrives, which is exactly the silence L0 was built to fill.
  const showLiveActivity = isStreaming && activity?.startedAt != null;

  // An empty list renders nothing; the surrounding shell body owns the
  // empty-state UX (the passive bot placeholder was retired).
  if (messages.length === 0 && !isStreamingActive && !showLiveActivity && !error) {
    return null;
  }

  // Filter out `toolResult` (and any future non-user/non-assistant
  // roles): the gateway persists these as separate messages carrying
  // the raw tool output (e.g. a `read` of BOOTSTRAP.md returns the
  // file's full text), which would render as a wall of prose between
  // the user's prompt and the assistant's actual answer. The tool's
  // *call* is already surfaced as an inline card inside the assistant
  // message (via BlockRenderer's tool_use dispatcher), so the user
  // sees `Tool · read [path]` without the raw payload bloat.
  const visibleMessages = messages.filter(
    (m) => m.role === "user" || m.role === "assistant",
  );

  // Index of the most recent assistant turn — the only message that
  // gets the retry affordance (see prop doc). Retry is suppressed while a
  // turn streams (`isStreaming`) or while the opening hasn't landed.
  // System receipts are `role: "assistant"` on the wire but are NOT a turn the
  // lawyer can retry — and since they arrive unprompted (an upload finishing),
  // counting them here would silently move the retry affordance off the real
  // last answer and onto a machine line that renders no affordance at all. The
  // net effect would be "retry vanished after I attached a file".
  let lastAssistantIdx = -1;
  for (let i = visibleMessages.length - 1; i >= 0; i--) {
    const m = visibleMessages[i];
    if (m.role === "assistant" && !isSystemReceipt(m)) {
      lastAssistantIdx = i;
      break;
    }
  }

  return (
    // Reading column: 80% of the shell, centred — 10% breathing room each side
    // (operator 2026-07-27, replacing the fixed 720px cap from plan §3/§6.6).
    // Percentage rather than a px cap so the transcript grows with the window
    // instead of stranding the lawyer in a narrow ribbon on a wide screen.
    //
    // The `%` is safe under `ConversationShell`'s zoom wrapper, which is the
    // non-obvious part: that wrapper sets `width: box.w / zoom` px, so a
    // percentage inside it resolves against the UNZOOMED width and is then
    // scaled back by `zoom` — netting the same 80% of the visible box at every
    // zoom level. (A `%` under a bare `zoom`, with no compensating width, would
    // NOT behave this way.) Verified in the browser at both zoom levels.
    //
    // The composer keeps its own fixed `COMPOSER_MAX_W` (720px) deliberately —
    // operator put the promptbox explicitly out of scope. The two columns
    // therefore align at exactly one shell width (720/0.8 = 900px): below it the
    // composer is the wider of the two, above it the transcript is. That is a
    // consequence of the ruling, not an oversight; revisit here only if the
    // promptbox comes back into scope.
    <div className="mx-auto flex w-4/5 flex-col gap-6 px-4 py-5">
      {visibleMessages.map((msg, i) => (
        <MessageBubble
          key={`${msg.runId ?? i}-${msg.role}`}
          message={msg}
          onRetry={onRetry && !isStreaming && !error && i === lastAssistantIdx ? onRetry : undefined}
        />
      ))}

      {/* The forming turn — L0's in-flow status line + L1's live tool steps,
          then the answer as it streams beneath them.

          This lives INSIDE the reading-column div on purpose: that div is the
          element `useStickToBottom` observes, so everything here appearing and
          growing is re-pinned by a mechanism that already respects a reader who
          scrolled up. Do not lift it into `ConversationShell` — the retired
          fixed cue sat there, outside the scroll body, beyond that observer.

          Phase 10.B intentionally omits the feedback chip here: the assistant's
          runId is not yet final mid-stream, so a thumb click would key into our
          5s dedup window on `(userId, surface, runId, polarity)` against an
          undefined runId — every subsequent thumb on a different in-flight
          stream inside that window would 409-collide. */}
      {(showLiveActivity || isStreamingActive) && (
        <div data-testid="streaming-assistant" data-role="assistant" className="flex flex-col gap-3">
          {showLiveActivity && activity && (
            <TurnActivity
              live
              blocks={streamingBlocks.filter((b) => b.type === "tool_use")}
              startedAt={activity.startedAt}
              stalled={stalled}
              onRelease={onReleaseStalledRun}
              {...(activity.workedMs !== null ? { workedMs: activity.workedMs } : {})}
              outcomes={activity.outcomes}
            />
          )}
          {isStreamingActive && (
            <div className={ASSISTANT_PROSE}>
              {streamingBlocks
                .filter((b) => b.type !== "tool_use")
                .map((block, i) => (
                  <BlockRenderer key={i} block={block} parseInlineImages streaming={isStreaming} renderMarkdown />
                ))}
              {isStreaming && <StreamingCursor />}
            </div>
          )}
        </div>
      )}

      {/* Error display — semantic critical (plan §5 keeps healthy/warning/
          critical), no decorative tint. A failed turn is the canonical
          retry moment, so the retry control lives HERE when an error is
          present (the per-message retry above is suppressed while `error`
          is set, so there's exactly one affordance). `addUserMessage`
          clears `error`, so the card disappears the instant retry fires. */}
      {error &&
        (typeof error === "object" ? (
          // Classified provider error → localized, actionable card.
          <ChatLLMProviderErrorCard error={error} onRetry={onRetry} isStreaming={isStreaming} />
        ) : (
          // Legacy flat-string fallback (unclassified failures).
          <div
            data-testid="chat-error"
            className="rounded-lg bg-red-50 px-3 py-2 text-body-sm text-red-600 dark:bg-red-900/20 dark:text-red-400"
          >
            <div>{error}</div>
            {onRetry && !isStreaming && (
              <button
                type="button"
                data-testid="chat-error-retry"
                onClick={onRetry}
                className="mt-1.5 inline-flex items-center gap-1 rounded text-body-sm font-medium text-red-700 transition-colors hover:text-red-900 dark:text-red-300 dark:hover:text-red-200"
              >
                <RotateCcw size={13} />
                {t("llmErrors.llm.retry")}
              </button>
            )}
          </div>
        ))}

    </div>
  );
}

/** User-turn block (Claude-style):
 *  - right-aligned filled box, NOT full reading-column width — the
 *    user prompt is supporting context, the AI text is the main
 *    content. Sans-serif (DM Sans, the body font) keeps the prompt
 *    visually distinct + less prominent than the assistant's serif
 *    prose.
 *  - body height capped at `MAX_USER_HEIGHT_PX`; an overflow
 *    measurement on first paint decides whether to surface the
 *    "Show more" / "Show less" toggle
 *  - latched: once `overflows` is true (clipped at first paint),
 *    it stays true so toggling to "expanded" doesn't make the
 *    button vanish (it just changes label to "Show less")
 */
const MAX_USER_HEIGHT_PX = 200;

/**
 * A line the RUNTIME injected into the conversation — today, the ingest
 * receipts (R1 "📎 Recebi …", R2 "✅ … processado", "⚠️ … não pôde ser
 * processado").
 *
 * Register is the whole point (ingest-flow spec G2 ★). These are events, not
 * utterances: recessive weight, smaller type, a left rule to set them off the
 * reading column, and none of the assistant's affordances. The lawyer should be
 * able to tell at a glance that the system is reporting a fact rather than the
 * assistant making a promise — the previous build read as the concierge saying
 * "aviso aqui" on the model's behalf, which it had no way to honour.
 *
 * The text already carries its own leading emoji from the runtime, so no icon
 * is added here: a second glyph would just compete with it.
 */
/**
 * Strip `**` emphasis markers from a receipt's text.
 *
 * The runtime writes receipts as markdown (`✅ **<file>** processado`), but
 * `ProseBlock` deliberately does NOT parse general markdown — it is a targeted
 * image/file-chip affordance, not a markdown pipe (see its `parseInlineImages`
 * docblock). So the asterisks rendered LITERALLY: `** [chip] ** processado`.
 *
 * Pre-existing — receipts looked like this as assistant bubbles too — but the
 * system-event register puts them on a short, sparse line where the noise is
 * unmissable, so F1 would be shipping a visibly broken line without this.
 *
 * Strips rather than renders bold, deliberately: emphasis inside a recessive
 * system line is the opposite of what the register is for, and the file chip
 * already carries the visual weight the `**` was reaching for. Confined to this
 * component — the DURABLE text keeps its markers, because other channels
 * (WhatsApp/Telegram) render markdown natively and the copy is not mine.
 */
function stripEmphasisMarkers(block: ContentBlock): ContentBlock {
  if (block.type !== "text" || !block.text.includes("**")) return block;
  return { ...block, text: block.text.replace(/\*\*/g, "") };
}

function SystemEventLine({ message }: { message: ChatMessage }) {
  return (
    <div
      data-role="system-event"
      data-testid="chat-system-event"
      className="flex flex-col gap-1"
    >
      <div className="min-w-0 border-l-2 border-cream-300 py-0.5 pl-3 font-sans text-body-sm font-light whitespace-pre-wrap text-coffee-700 dark:border-coffee-700 dark:text-cream-300">
        {message.content.map((block, i) => (
          <BlockRenderer
            key={i}
            block={stripEmphasisMarkers(block)}
            runId={message.runId}
            parseInlineImages
          />
        ))}
      </div>
    </div>
  );
}

function UserMessage({ message }: { message: ChatMessage }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Voice-note presentation (operator-approved "variant B", 2026-07-02):
  // audio attachments render as a header INSIDE the bubble (mic + "voz"
  // + play on a divider line) with the transcript in italics below —
  // an unequivocal "this is a transcription" signal. Non-audio
  // attachments keep the chip echo below the bubble. `message.voice`
  // (promptbox mic dictation, auto-submitted) gets the SAME bubble —
  // playback comes from the session-local `audioUrl` instead of the
  // lazy `lx.chat_read_media` fetch.
  const audioAttachments = (message.attachments ?? []).filter(isAudioAttachment);
  const otherAttachments = (message.attachments ?? []).filter(
    (a) => !isAudioAttachment(a),
  );
  const isVoiceNote = audioAttachments.length > 0 || message.voice != null;

  // Selection echo (operator brief 2026-06-28 + handoff
  // `lexide-retire-sent-selection-store-handoff-2026-06-29.md`):
  // `message.selection` is the SINGLE source of truth. It's stamped:
  //   • LIVE — by `useChatStore.addUserMessage` when ChatInput passes
  //     a `sentSelection` to `onSend(..., undefined, selection)`;
  //   • RELOAD — by `useChatSession`'s history loader mapping the
  //     server-persisted `LxConversationMessage.context` onto the
  //     ChatMessage shape.
  // Either way the bubble renders the echo straight from the field —
  // no in-memory FIFO, no content-key matching, no useState bridge.

  useLayoutEffect(() => {
    if (!ref.current || expanded) return;
    const node = ref.current;
    setOverflows(node.scrollHeight > node.clientHeight + 2);
  }, [message.content, expanded]);

  return (
    <div data-role="user" className="flex flex-col items-end gap-1">
      <div
        ref={ref}
        style={!expanded ? { maxHeight: MAX_USER_HEIGHT_PX } : undefined}
        className={cn(
          // Color shades matched to Claude's reference: light-mode box
          // sits one shade off the cream-50 page (bg-cream-200 #e8e6e0)
          // with softened dark text (coffee-700 #3b342d) — not a hard
          // black. Dark mode uses coffee-700 (#3b342d, two shades lighter
          // than the coffee-900 page) so the bubble is clearly framed
          // against the dark canvas with cream-200 text for comfortable
          // reading. The bubble stays distinctly subordinate to the
          // assistant's serif prose (which is the main content).
          "max-w-[85%] whitespace-pre-wrap rounded-2xl bg-cream-200 px-4 py-3 text-body text-coffee-700 dark:bg-coffee-700 dark:text-cream-200",
          !expanded && "overflow-hidden",
        )}
      >
        {isVoiceNote && (
          <VoiceNoteHeader
            attachments={audioAttachments}
            localAudioUrl={message.voice?.audioUrl}
          />
        )}
        <div className={cn(isVoiceNote && "italic")}>
          {message.content.map((block, i) => (
            <BlockRenderer key={i} block={block} runId={message.runId} stripUserPreamble />
          ))}
        </div>
      </div>
      {message.selection && (
        <UserMessageSelectionEcho selection={message.selection} />
      )}
      {message.mentions && message.mentions.length > 0 && (
        <UserMessageMentionsEcho mentions={message.mentions} />
      )}
      {otherAttachments.length > 0 && (
        <UserMessageAttachmentsEcho attachments={otherAttachments} />
      )}
      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="user-message-expand"
          className="pr-1 text-caption text-cream-500 underline-offset-4 transition-colors hover:underline focus-visible:underline dark:text-white/60"
        >
          {expanded ? t("chat.message.collapse") : t("chat.message.expand")}
        </button>
      )}
    </div>
  );
}

/**
 * Subtitle rendered below a user-message bubble when the turn was
 * sent WITH an active selection chip. Reads `message.selection`
 * directly (no in-memory FIFO bridge) — see UserMessage for the
 * source-of-truth note. The preview is computed inline via
 * `buildSelectionPreview(text, 50, 49)` — the SAME 100-char
 * middle-ellipsis shape the chip showed live, so the chat-history
 * echo and the composer chip read identically.
 *
 * Visual: Quote icon + small italic muted text, max 2 lines (per the
 * operator's "compact echo" brief, 5th refinement).
 */
function UserMessageSelectionEcho({
  selection,
}: {
  selection: { source: string; text: string };
}) {
  const preview = buildSelectionPreview(selection.text, 50, 49);
  return (
    <div
      data-testid="user-message-selection-echo"
      data-source={selection.source}
      className="flex max-w-[85%] items-start gap-1.5 pr-1 text-caption italic text-cream-500 dark:text-cream-500"
      title={preview}
    >
      <Quote size={10} className="mt-[3px] shrink-0 text-gold-700/80 dark:text-gold-400/80" />
      <span className="min-w-0 flex-1 whitespace-pre-wrap break-words line-clamp-2">
        {preview}
      </span>
    </div>
  );
}

/**
 * Referenced-file echo rendered below a user-message bubble when the turn
 * was sent WITH @-mention / drag-drop workspace-file references. Each chip
 * opens the file in the workspace pane on click — the analogue of the
 * selection echo, but for the third rich-context instance (handoff
 * `lexide-mention-workspace-files-handoff-2026-06-29.md` §4.6).
 *
 * LIVE-only: `message.mentions` is stamped optimistically by
 * `addUserMessage`; it is not server-persisted, so the echo is present
 * for the active session but absent after a reload (handoff §7 non-goal).
 */
function UserMessageMentionsEcho({
  mentions,
}: {
  mentions: { path: string; name: string }[];
}) {
  const openFile = useWorkspaceFileStore((s) => s.openFile);
  // Resolve each historical mention at RENDER time (tag-first + rule-6) — the echo
  // stored only {path, name}, so the friendly tag/title come from the live index.
  const map = useWorkspaceAliasMap();
  return (
    <div
      data-testid="user-message-mentions-echo"
      className="flex max-w-[85%] flex-wrap justify-end gap-1.5 pr-1"
    >
      {mentions.map((m) => {
        const entry = map.byPath.get(m.path) ?? map.resolve(m.path);
        const friendly = entry?.title?.trim() || entry?.alias?.trim();
        return (
          <MentionChip
            key={m.path}
            name={m.name}
            path={m.path}
            tag={entry?.tag}
            title={entry?.title ?? entry?.alias}
            onOpen={(el) => openFile({ path: m.path, title: friendly || m.name }, el)}
          />
        );
      })}
    </div>
  );
}

/**
 * Conversation-attachment echo rendered below a user-message bubble — the
 * actual FILE a turn was sent with (today: the audio an IM voice-note
 * transcript came from; the runtime keeps a durable copy and serves it via
 * `lx.chat_read_media`). Rehydrated from `lx.chat_history` on reload, so —
 * unlike the mentions echo — this one IS durable.
 *
 * Audio attachments get a click-to-load inline player (bytes are fetched
 * lazily on first play, then cached as a data URL). Non-audio kinds render
 * as a plain chip (name only) until a preview flow ships.
 */
function UserMessageAttachmentsEcho({
  attachments,
}: {
  attachments: NonNullable<ChatMessage["attachments"]>;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="user-message-attachments-echo"
      role="group"
      aria-label={t("chat.message.attachmentsEchoAria")}
      className="flex max-w-[85%] flex-col items-end gap-1 pr-1"
    >
      {attachments.map((a) => (
        <AttachmentEchoChip key={a.name} attachment={a} />
      ))}
    </div>
  );
}

/**
 * Voice-note header rendered INSIDE the user bubble (operator-approved
 * "variant B", 2026-07-02): mic glyph + a tiny uppercase "voz"/"voice"
 * label + right-aligned play affordance, separated from the italic
 * transcript by a subtle divider. Unmistakably marks the bubble text
 * as a TRANSCRIPTION of the attached audio rather than typed input.
 * Durable across reload (rides `lx.chat_history` like the chip did).
 */
function VoiceNoteHeader({
  attachments,
  localAudioUrl,
}: {
  attachments: NonNullable<ChatMessage["attachments"]>;
  /** Session-local object URL of a promptbox mic dictation — renders a
   *  direct inline player row (no lazy fetch; the clip only lives in
   *  this page lifetime). */
  localAudioUrl?: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="voice-note-header"
      className="mb-2 flex flex-col gap-1 border-b border-coffee-700/15 pb-1.5 not-italic dark:border-cream-200/15"
    >
      {attachments.map((a) => (
        <VoiceNoteHeaderRow key={a.name} attachment={a} />
      ))}
      {localAudioUrl && (
        <div className="flex items-center gap-1.5 opacity-80" data-testid="voice-note-local-row">
          <Volume2 size={11} className="shrink-0" aria-hidden />
          <span className="text-[9px] font-semibold uppercase tracking-[0.08em]">
            {t("chat.message.voiceShort")}
          </span>
          <span className="flex-1" />
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- the transcript below IS the caption */}
          <audio controls src={localAudioUrl} className="h-6 max-w-[220px]" />
        </div>
      )}
    </div>
  );
}

function VoiceNoteHeaderRow({
  attachment,
}: {
  attachment: NonNullable<ChatMessage["attachments"]>[number];
}) {
  const { t } = useTranslation();
  const { src, state, load } = useAttachmentAudio(attachment);
  return (
    <div className="flex items-center gap-1.5 opacity-80" title={attachment.name}>
      <Volume2 size={11} className="shrink-0" aria-hidden />
      <span className="text-[9px] font-semibold uppercase tracking-[0.08em]">
        {t("chat.message.voiceShort")}
      </span>
      <span className="flex-1" />
      {state !== "ready" && (
        <button
          type="button"
          onClick={load}
          disabled={state === "loading"}
          aria-label={
            state === "error"
              ? t("chat.message.attachmentRetry", { name: t("chat.message.voiceShort") })
              : t("chat.message.attachmentPlay", { name: t("chat.message.voiceShort") })
          }
          data-testid="voice-note-play"
          className="inline-flex items-center text-gold-700 transition-colors hover:text-gold-600 disabled:opacity-50 dark:text-gold-400"
        >
          {state === "loading" ? (
            <RotateCcw size={12} className="animate-spin" />
          ) : state === "error" ? (
            <RotateCcw size={12} />
          ) : (
            <Play size={12} />
          )}
        </button>
      )}
      {state === "ready" && src && (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- the transcript below IS the caption
        <audio controls autoPlay src={src} className="h-6 max-w-[220px]" />
      )}
    </div>
  );
}

function MessageBubble({ message, onRetry }: { message: ChatMessage; onRetry?: () => void }) {
  const isUser = message.role === "user";
  const plain = plainTextOf(message);

  // User turn (Claude-style): a wide filled box (fills the reading
  // column, NOT right-aligned + max-w-85% like a chat bubble) with the
  // body height capped at ~7 lines + a Show more / Show less toggle
  // when content overflows. Long prompts no longer push the assistant's
  // answer off the visible viewport.
  if (isUser) {
    return <UserMessage message={message} />;
  }

  // no_answer marker (7802ab6d0, ruled 2026-08-14): the turn's agent chain
  // died without producing an answer, and the runtime's settle sweep recorded
  // that ONCE, ~10 min later. The row's `text` is EMPTY by design (codes only
  // ever cross the wire; wording is ours), so without this branch it would
  // render as a blank assistant bubble — the very "indistinguishable from
  // being ignored" state the marker exists to end.
  //
  // 🚨 Switch on the VALUE. A healthy compaction segment also ends with empty
  // text, so emptiness must never be the discriminator. An absent `cause`
  // renders the NEUTRAL wording — never a confident one.
  if (message.kind === "no_answer") {
    return <NoAnswerMarker message={message} onRetry={onRetry} />;
  }

  // kind:"cancelled" (R6b, 54843fbde) — the lawyer stopped their own mid-run
  // turn. An ACT, not a fault: the ruling's whole point is that this renders as
  // a light interrupted state and NEVER as an error card (it used to persist as
  // an error row saying "This operation was aborted" — the mislabelling R6b
  // removes). Retry stays; there is nothing to copy or evaluate.
  if (message.kind === "cancelled") {
    return <CancelledMarker message={message} onRetry={onRetry} />;
  }

  // G2 ★ — a runtime-INJECTED line (ingest receipts) is not the concierge
  // speaking, so it must not wear the concierge's register. The spec's rejected
  // alternative was exactly this rendered as a normal bubble: "the model may
  // believe it already responded, and the visual register lies".
  //
  // Deliberately WITHOUT the copy + thumbs action row: those say "rate what the
  // assistant told you", and there is no assistant utterance here to rate. A
  // feedback thumb on a machine receipt is a category error, not a missing
  // feature.
  if (isSystemReceipt(message)) {
    return <SystemEventLine message={message} />;
  }

  // Assistant turn (plan §6.6) — NO bubble: prose lives directly on the
  // cream canvas with a slim neutral left-accent for scanability. The
  // `group` enables the hover-reveal feedback chip below the prose.
  //
  // Rich-content-model: text blocks render as prose; tool_use blocks
  // render as INLINE cards (TodoCard, EditBlock, etc.) via the
  // BlockRenderer dispatcher. Inline = the card lives INSIDE the
  // assistant bubble's DOM subtree (data-role="assistant"), which the
  // e2e spec asserts.
  // The turn's activity trail is the LEADING run of tool_use blocks (the order
  // `finalizeRun` merges them in); everything after it is the answer. The trail
  // renders as the collapsed "Trabalhou por X ›" disclosure above the prose.
  const { trail, answer } = splitTurnActivity(message.content);

  // A RELOADED trail-bearing (v>=2) turn carries its persisted trail; a
  // live-finalized turn carries arg-bearing blocks. Exactly one is ever
  // present, and they render differently on purpose — the persisted entries
  // have no args to dispatch on (D5), so they get compact rows rather than
  // synthesized tool cards.
  const persistedTrail = message.toolTrail;
  const hasSteps = persistedTrail ? persistedTrail.length > 0 : trail.length > 0;

  return (
    <div data-role="assistant" className="group flex flex-col gap-3">
      {(hasSteps || typeof message.workedMs === "number") && (
        <TurnActivity
          blocks={trail}
          {...(persistedTrail ? { trail: persistedTrail } : {})}
          {...(typeof message.workedMs === "number" ? { workedMs: message.workedMs } : {})}
          {...(message.outcomes ? { outcomes: message.outcomes } : {})}
          {...(message.trailAttribution ? { attribution: message.trailAttribution } : {})}
          runId={message.runId}
        />
      )}
      <div className={ASSISTANT_PROSE}>
        {answer.map((block, i) => (
          <BlockRenderer key={i} block={block} runId={message.runId} parseInlineImages renderMarkdown />
        ))}
      </div>
      {/* Per-response action row — ALWAYS visible under each AI response
          (operator directive): completion timestamp + copy-to-clipboard +
          thumbs feedback. The thumbs render only when runId is final —
          older/system messages lack it and the dedup key would collapse to
          "undefined" and 409-collide within the 5s window; copy has no such
          requirement.

          ⚠ The thumbs already persist: `FeedbackButton` POSTs one record per
          click to lexserver `POST /api/feedback` (dedup-keyed on
          `(userId, surface, runId, polarity)`, reviewed in the org-admin
          FeedbackPage). D6's "record as a user-activity event" recommendation
          is SUPERSEDED by that shipped pipeline — a second sink here would
          DOUBLE-RECORD every evaluation. Do not add one. */}
      {/* Flush LEFT with the prose above (operator, 2026-08-13). `pl-3` indented
          the row 12px past the text; the icon buttons then add their own ~6px of
          internal centring (24px box around a 13px glyph), so the visible icons
          sat well right of where the answer starts. The negative margin cancels
          that internal inset, aligning the GLYPH — which is what the eye reads
          as the edge — rather than the button box. */}
      <div className="-ml-1.5 flex items-center gap-1">
        <CopyButton text={plain} />
        {message.runId && (
          <FeedbackButton
            surface="chat.message"
            context={{
              runId: message.runId,
              messageRole: "assistant",
              messageExcerpt: plain.slice(0, 500),
            }}
            size="sm"
          />
        )}
        {onRetry && <RetryButton onRetry={onRetry} />}
        {/* Immediately RIGHT OF THE ICONS (operator, 2026-08-13) — not flung to
            the far edge. `ml-auto` was the first reading of "rightmost" and it
            stranded the time against the pane border, visually detached from the
            row it belongs to. It trails the controls with a small gap instead. */}
        <CompletionTimestamp timestamp={message.timestamp} />
      </div>
    </div>
  );
}

/** When the turn landed — the row's own `ts`, locale-formatted ("18:35").
 *
 *  The value has ALWAYS been persisted (both the live finalize and the
 *  `lx.chat_history` reload stamp it); nothing rendered it. This is the only
 *  genuinely missing piece of the completion footer — the action buttons and
 *  the feedback pipeline behind them already shipped.
 *
 *  Renders nothing on an unparseable timestamp rather than "Invalid Date": a
 *  malformed row is a fact we cannot report, not a time to invent. */
function CompletionTimestamp({ timestamp }: { timestamp: string }) {
  const { i18n } = useTranslation();
  const ms = Date.parse(timestamp);
  if (!Number.isFinite(ms)) return null;
  return (
    <time
      data-testid="chat-message-timestamp"
      dateTime={new Date(ms).toISOString()}
      // Full precision stays one hover away — the label itself is lossy by design.
      title={formatMessageTimestampFull(ms, i18n.language)}
      // ⚠ VERTICALLY CENTRED — the operator's final ruling (2026-08-14, "we
      // want the middle") after bottom-pinning read too low. `items-center` on
      // the row + `leading-none` here IS the geometric middle: a 12px line box
      // centred in the 24px icon buttons. No nudges, no self-end — the row's
      // own centring is the mechanism, and the verification standard for any
      // future change here is a SCREENSHOT read by eye, not a coordinate delta
      // (three measured-and-"passing" attempts preceded this line).
      className="ml-2 select-none text-caption font-light leading-none tabular-nums text-cream-500 dark:text-cream-500"
    >
      {formatMessageTimestamp(ms, i18n.language)}
    </time>
  );
}

/** Copy-to-clipboard for an assistant response — flips to a gold check
 *  for ~1.5s on success. Warm ghost icon button. */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid="chat-message-copy"
      aria-label="Copiar resposta"
      title="Copiar"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable (insecure context / denied) — no-op */
        }
      }}
      className="flex h-6 w-6 items-center justify-center rounded text-cream-500 transition-colors hover:bg-cream-100/60 hover:text-coffee-700 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200"
    >
      {copied ? <Check size={13} className="text-gold-600" /> : <Copy size={13} />}
    </button>
  );
}

/** Retry the most recent turn — re-sends the last user prompt as a fresh
 *  turn (stopgap, not a true in-place regenerate; see useChatSession). Warm
 *  ghost icon button matching CopyButton. */
function RetryButton({ onRetry }: { onRetry: () => void }) {
  return (
    <button
      type="button"
      data-testid="chat-message-retry"
      aria-label="Tentar novamente"
      title="Tentar novamente"
      onClick={onRetry}
      className="flex h-6 w-6 items-center justify-center rounded text-cream-500 transition-colors hover:bg-cream-100/60 hover:text-coffee-700 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200"
    >
      <RotateCcw size={13} />
    </button>
  );
}

function StreamingCursor() {
  return (
    <span className="ml-0.5 inline-block h-3 w-1.5 animate-pulse rounded-sm bg-cream-500 align-middle" />
  );
}

/**
 * The "turn ended without an answer" notice (kind: "no_answer").
 *
 * A muted card in the transcript's own register — not an error card (the
 * classified error path is for live failures with affordances) and not prose
 * (the concierge never said this). Copy/feedback are deliberately absent:
 * there is nothing to copy and nothing to evaluate. Retry stays when offered —
 * re-asking is exactly the recovery this state wants.
 */
function NoAnswerMarker({ message, onRetry }: { message: ChatMessage; onRetry?: () => void }) {
  const { t, i18n } = useTranslation();
  const ms = Date.parse(message.timestamp);
  return (
    <div data-role="assistant" data-kind="no_answer" className="group flex flex-col gap-2">
      <div className="flex items-start gap-2 rounded-md border border-cream-300 bg-cream-100/30 px-3 py-2 text-body-sm text-coffee-600 dark:border-coffee-700 dark:bg-coffee-900/30 dark:text-cream-400">
        <MessageSquareOff size={15} className="mt-0.5 shrink-0" />
        <span>
          {message.cause === "overflow"
            ? t("chat.noAnswer.overflow")
            : t("chat.noAnswer.neutral")}
        </span>
      </div>
      <div className="-ml-1.5 flex items-center gap-1">
        {onRetry && <RetryButton onRetry={onRetry} />}
        {Number.isFinite(ms) && <CompletionTimestamp timestamp={message.timestamp} />}
      </div>
    </div>
  );
}

/** The "you stopped this reply" notice (kind: "cancelled", R6b) — lighter than
 *  `NoAnswerMarker` by design: no card border, just a quiet line. The lawyer
 *  did this on purpose; the transcript acknowledges it without commentary. */
function CancelledMarker({ message, onRetry }: { message: ChatMessage; onRetry?: () => void }) {
  const { t } = useTranslation();
  const ms = Date.parse(message.timestamp);
  return (
    <div data-role="assistant" data-kind="cancelled" className="group flex flex-col gap-2">
      <div className="flex items-center gap-2 text-body-sm italic text-cream-500 dark:text-cream-500">
        <CircleStop size={14} className="shrink-0" />
        <span>{t("chat.cancelled.notice")}</span>
      </div>
      <div className="-ml-1.5 flex items-center gap-1">
        {onRetry && <RetryButton onRetry={onRetry} />}
        {Number.isFinite(ms) && <CompletionTimestamp timestamp={message.timestamp} />}
      </div>
    </div>
  );
}
